import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import PageLayout from '../components/PageLayout';
import NativeAdCard from '../components/NativeAdCard';
import QuizCTABanner from '../components/QuizCTABanner';
import Breadcrumbs from '../components/Breadcrumbs';
import { useAds } from '../hooks/useAds';
import { useRecentlyVisited } from '../hooks/useRecentlyVisited';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, ArrowLeft, Heart, Share2, BookOpen, Plane, Lock } from 'lucide-react';
import { BlogSEO } from '../components/SEOHead';
import AuthModal from '../components/AuthModal';
import { motion } from 'framer-motion';
import { PostCard } from './Blog';

import { API } from '../config';

export default function BlogPost() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { add: addVisit } = useRecentlyVisited();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [morePosts, setMorePosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [sharesCount, setSharesCount] = useState(0);
  const [liking, setLiking] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const ads = useAds('blog');

  useEffect(() => {
    setLoading(true);
    setRelated([]);
    setMorePosts([]);
    axios.get(`${API}/blog/${slug}`, { withCredentials: true })
      .then(async r => {
        const p = r.data;
        setPost(p);
        setLiked(p.user_liked || false);
        setLikeCount(p.like_count || 0);
        setSharesCount(p.shares_count || 0);
        setLoading(false);
        // Track visit
        addVisit({ type: 'blog', href: `/blog/${slug}`, label: p.title, sublabel: p.tags?.[0] || 'Blog' });

        // Fetch related posts by first tag
        const firstTag = (p.tags || [])[0];
        if (firstTag) {
          try {
            const rel = await axios.get(`${API}/blog`, { params: { tag: firstTag, limit: 6 } });
            const items = Array.isArray(rel.data) ? rel.data : (rel.data.posts || []);
            setRelated(items.filter(x => x.slug !== slug).slice(0, 4));
          } catch {}
        }

        // Fetch more recent posts
        try {
          const more = await axios.get(`${API}/blog`, { params: { limit: 7 } });
          const items = Array.isArray(more.data) ? more.data : (more.data.posts || []);
          setMorePosts(items.filter(x => x.slug !== slug).slice(0, 3));
        } catch {}
      })
      .catch(() => setLoading(false));
  }, [slug]);

  const handleLike = async () => {
    if (!user) { setShowAuthModal(true); return; }
    setLiking(true);
    try {
      const res = await axios.post(`${API}/blog/${slug}/like`, {}, { withCredentials: true });
      setLiked(res.data.liked);
      setLikeCount(res.data.like_count);
      toast.success(res.data.liked ? 'Article liked!' : 'Like removed');
    } catch { toast.error('Failed to like'); }
    finally { setLiking(false); }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: post?.title, url: window.location.href });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied!');
      }
    } catch {}
    try {
      const res = await axios.post(`${API}/blog/${slug}/share`);
      setSharesCount(res.data.shares_count);
    } catch {}
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="max-w-5xl mx-auto px-4 py-8 space-y-4">
          <Skeleton className="h-8 w-48 rounded-xl" />
          <Skeleton className="h-72 rounded-2xl" />
          <div className="grid lg:grid-cols-[1fr_280px] gap-8">
            <Skeleton className="h-96 rounded-2xl" />
            <Skeleton className="h-64 rounded-2xl" />
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!post) {
    return (
      <PageLayout>
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <p className="text-muted-foreground">Post not found</p>
          <Button className="mt-4 fly-btn-primary" asChild><Link to="/blog">Back to Blog</Link></Button>
        </div>
      </PageLayout>
    );
  }

  const paragraphs = post.content ? post.content.split('\n') : [];
  const midPoint = Math.max(4, Math.floor(paragraphs.length * 0.4));
  // For non-logged users, only render first 40% of paragraphs then show gate
  const visibleParagraphs = !user && paragraphs.length > 8
    ? paragraphs.slice(0, Math.max(6, Math.floor(paragraphs.length * 0.4)))
    : paragraphs;

  return (
    <PageLayout>
      <BlogSEO post={post} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">

        {/* Breadcrumb navigation */}
        <Breadcrumbs
          className="mb-6"
          items={[
            { label: 'Blog', href: '/blog' },
            { label: post.title },
          ]}
        />

        {/* Cover image — full width */}
        {post.cover_image && (
          <div className="h-64 sm:h-80 rounded-2xl overflow-hidden mb-8">
            <img
              src={post.cover_image}
              alt={post.title}
              className="w-full h-full object-cover"
              onError={e => { e.target.style.display = 'none'; }}
            />
          </div>
        )}

        {/* Tags + Title + Meta */}
        <div className="flex flex-wrap gap-2 mb-4">
          {post.tags?.map(tag => (
            <Badge key={tag} className="text-xs bg-[#1E3A8A]/10 text-[#1E3A8A] border-[#1E3A8A]/20">{tag}</Badge>
          ))}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold mb-4 leading-tight" style={{ fontFamily: 'Space Grotesk' }}>{post.title}</h1>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8 pb-8 border-b border-border">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {post.read_time} min read</span>
            <span>{post.created_at ? new Date(post.created_at).toLocaleDateString() : ''}</span>
          </div>
          <div className="flex items-center gap-2">
            <motion.button whileTap={{ scale: 0.9 }} onClick={handleLike} disabled={liking}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${liked ? 'bg-red-50 border-red-200 text-red-500 dark:bg-red-950/30 dark:border-red-800' : 'bg-muted border-border text-muted-foreground hover:text-[#FF4500] hover:border-[#FF4500]/30'}`}
              data-testid="blog-like-btn">
              <Heart className={`w-4 h-4 ${liked ? 'fill-red-500 text-red-500' : ''}`} />
              <span>{likeCount}</span>
            </motion.button>
            <Button variant="outline" size="sm" onClick={handleShare} className="rounded-full" data-testid="blog-share-btn">
              <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share {sharesCount > 0 && <span className="text-muted-foreground ml-1">· {sharesCount}</span>}
            </Button>
          </div>
        </div>

        {/* ── Two-column: article + sticky sidebar ── */}
        <div className="grid lg:grid-cols-[1fr_280px] gap-10 items-start">

          {/* ── LEFT: Article body ─────────────────── */}
          <article>
            <div className="prose prose-sm dark:prose-invert max-w-none space-y-0">
              {visibleParagraphs.map((para, i) => {
                const el = renderParagraph(para, i);
                // Inject mid-article ad after midPoint (only for logged-in users)
                if (user && i === midPoint && ads.length > 0) {
                  return (
                    <React.Fragment key={`frag-${i}`}>
                      {el}
                      <MidArticleAd ad={ads[0]} />
                    </React.Fragment>
                  );
                }
                return el;
              })}
            </div>

            {/* Content gate for non-logged users — fade after 40% of paragraphs */}
            {!user && paragraphs.length > 8 && (
              <div className="relative mt-0">
                {/* Fade overlay starting from mid-article */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: 'linear-gradient(to bottom, transparent 0%, var(--background) 70%)',
                    top: '-200px',
                    zIndex: 10,
                  }}
                />
                <div className="relative z-20 flex justify-center pt-4 pb-8">
                  <Card className="fly-card p-6 mx-4 w-full max-w-md text-center shadow-xl border-[#FF4500]/20">
                    <div className="w-10 h-10 rounded-full bg-[#FF4500]/10 flex items-center justify-center mx-auto mb-3">
                      <Lock className="w-5 h-5 text-[#FF4500]" />
                    </div>
                    <h3 className="font-bold text-base mb-1" style={{ fontFamily: 'Space Grotesk' }}>
                      Join Fly N Out to read the full article
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Create a free account to access all articles, community posts, and traveler insights.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2 justify-center">
                      <Button className="fly-btn-primary" onClick={() => setShowAuthModal(true)} data-testid="blog-gate-signup-btn">
                        Join Free
                      </Button>
                      <Button variant="outline" onClick={() => setShowAuthModal(true)}>
                        Log In
                      </Button>
                    </div>
                  </Card>
                </div>
              </div>
            )}

            {/* Bottom like/share bar */}
            <Card className="fly-card p-4 mt-10 flex items-center justify-between flex-wrap gap-3">
              <p className="text-sm text-muted-foreground">Found this helpful?</p>
              <div className="flex items-center gap-2">
                <motion.button whileTap={{ scale: 0.9 }} onClick={handleLike} disabled={liking}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-all ${liked ? 'bg-red-50 border-red-200 text-red-500 dark:bg-red-950/30 dark:border-red-800' : 'bg-muted border-border hover:text-[#FF4500] hover:border-[#FF4500]/30'}`}>
                  <Heart className={`w-4 h-4 ${liked ? 'fill-red-500 text-red-500' : ''}`} />
                  {liked ? 'Liked!' : 'Like this article'}
                  {likeCount > 0 && <span className="ml-1 text-muted-foreground">· {likeCount}</span>}
                </motion.button>
                <Button variant="outline" size="sm" onClick={handleShare} className="rounded-full">
                  <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
                </Button>
              </div>
            </Card>

            {/* Mobile-only ad below article */}
            {ads.length > 0 && (
              <div className="lg:hidden mt-6">
                <NativeAdCard ad={ads[0]} variant="blog" />
              </div>
            )}
          </article>

          {/* ── RIGHT: Sticky Sidebar (desktop) ───── */}
          <aside className="hidden lg:block">
            <div className="sticky top-20 space-y-6">
              {/* Ad card */}
              {ads.length > 0 && <NativeAdCard ad={ads[0]} variant="sidebar" />}

              {/* Quiz CTA */}
              <QuizCTABanner variant="sidebar" />

              {/* Related posts */}
              {related.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                    <BookOpen className="w-4 h-4 text-[#FF4500]" /> Related Articles
                  </h3>
                  <div className="space-y-3">
                    {related.slice(0, 3).map(p => (
                      <Link key={p.post_id} to={`/blog/${p.slug}`} className="group block">
                        <div className="flex gap-2.5 p-2.5 rounded-xl hover:bg-muted/60 transition-colors">
                          {p.cover_image && (
                            <img src={p.cover_image} alt="" className="w-14 h-12 rounded-lg object-cover flex-shrink-0" loading="lazy" onError={e => e.target.style.display='none'} />
                          )}
                          <div className="min-w-0">
                            <p className="text-xs font-semibold line-clamp-2 group-hover:text-[#FF4500] transition-colors leading-tight">{p.title}</p>
                            <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                              <Clock className="w-2.5 h-2.5" /> {p.read_time} min
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <Link to="/blog" className="mt-3 text-xs text-[#FF4500] hover:underline flex items-center gap-1">
                    View all articles →
                  </Link>
                </div>
              )}
            </div>
          </aside>
        </div>

        {/* ── You May Also Like ─────────────────────── */}
        {morePosts.length > 0 && (
          <section className="mt-16 pt-10 border-t border-border">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                <Plane className="w-5 h-5 text-[#FF4500]" /> You May Also Like
              </h2>
              <Link to="/blog" className="text-sm text-[#FF4500] hover:underline">View all →</Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {morePosts.map((p, i) => (
                <PostCard key={p.post_id} post={p} index={i} />
              ))}
            </div>
          </section>
        )}
      </div>

      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
    </PageLayout>
  );
}

/** Inline mid-article ad — styled as a clean reading-flow break */
function MidArticleAd({ ad }) {
  const handleClick = async () => {
    try { await axios.post(`${API}/ads/${ad.ad_id}/click`); } catch {}
    const url = ad.cta_url || '/contact';
    if (url.startsWith('/')) window.location.href = url;
    else window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="my-8">
      <button onClick={handleClick} className="w-full group text-left focus:outline-none" data-testid="mid-article-ad">
        <div className="relative rounded-2xl overflow-hidden border border-[#FF4500]/20 bg-gradient-to-r from-[#EBF4FF] to-[#F0F7FF] dark:from-[#1E3A5F]/20 dark:to-[#1E3A8A]/15 hover:border-[#FF4500]/40 transition-all">
          <div className="flex items-center gap-4 p-4">
            {ad.image_url && (
              <img src={ad.image_url} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" loading="lazy" onError={e => e.target.style.display='none'} />
            )}
            <div className="flex-1 min-w-0">
              <span className="text-[9px] font-semibold tracking-widest uppercase text-[#FF4500]/60 block mb-1">{ad.badge || 'Sponsored'}</span>
              <p className="text-sm font-bold text-foreground leading-tight mb-1" style={{ fontFamily: 'Space Grotesk' }}>{ad.title}</p>
              <p className="text-xs text-muted-foreground line-clamp-1">{ad.description}</p>
            </div>
            <div className="px-4 py-2 rounded-full bg-[#FF4500] text-white text-xs font-semibold whitespace-nowrap group-hover:bg-[#E03E00] transition-colors flex-shrink-0 hidden sm:block">
              {ad.cta_text || 'Learn More'}
            </div>
          </div>
        </div>
      </button>
    </div>
  );
}

/** Render a single paragraph/heading/list-item from markdown-lite content */
function renderParagraph(para, i) {
  if (para.startsWith('## '))
    return <h2 key={i} className="text-xl font-bold mt-8 mb-3" style={{ fontFamily: 'Space Grotesk' }}>{para.replace('## ', '')}</h2>;
  if (para.startsWith('### '))
    return <h3 key={i} className="text-lg font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk' }}>{para.replace('### ', '')}</h3>;
  if (para.startsWith('**') && para.endsWith('**'))
    return <p key={i} className="font-semibold mt-4 mb-1">{para.replace(/\*\*/g, '')}</p>;
  if (para.startsWith('- '))
    return <li key={i} className="ml-5 text-sm leading-relaxed list-disc">{para.replace('- ', '')}</li>;
  if (para.trim() === '')
    return <div key={i} className="h-3" />;
  return <p key={i} className="text-sm leading-relaxed mb-3 text-foreground/90">{para}</p>;
}
